import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import { getBookingCommissions } from '../../lib/bookingCommissions';

interface BookingProfitLossProps {
  revenue: number;
  materialCosts: number;
  laborCosts: number;
  discounts?: number;
  otherCosts?: number;
  bookingId?: string;
}

export default function BookingProfitLoss({
  revenue,
  materialCosts,
  laborCosts,
  discounts = 0,
  otherCosts = 0,
  bookingId
}: BookingProfitLossProps) {
  const [commissionCosts, setCommissionCosts] = useState(0);
  const [isLoadingCommissions, setIsLoadingCommissions] = useState(false);

  // Load commission costs when component mounts or bookingId changes
  useEffect(() => {
    const loadCommissionCosts = async () => {
      if (!bookingId) return;
      
      try {
        setIsLoadingCommissions(true);
        const commissions = await getBookingCommissions(bookingId);
        const totalCommissions = commissions.reduce((total, commission) => total + commission.commission_amount, 0);
        setCommissionCosts(totalCommissions);
      } catch (error) {
        console.error('Error loading commission costs:', error);
        setCommissionCosts(0);
      } finally {
        setIsLoadingCommissions(false);
      }
    };

    loadCommissionCosts();
  }, [bookingId]);

  const totalLaborCosts = laborCosts + commissionCosts;
  const totalCosts = materialCosts + totalLaborCosts + otherCosts + discounts;
  const profit = revenue - totalCosts;
  const profitMargin = revenue > 0 ? (profit / revenue) * 100 : 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <h3 className="text-sm font-medium text-gray-900">Profit & Loss</h3>
      </div>
      <div className="p-4 space-y-4">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Revenue</span>
          <span className="font-medium text-gray-900">${revenue.toLocaleString()}</span>
        </div>

        <div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Labor Costs</span>
            <span className="text-gray-900">-${totalLaborCosts.toLocaleString()}</span>
          </div>
          
          {/* Labor Cost Breakdown */}
          {(laborCosts > 0 || commissionCosts > 0) && (
            <div className="ml-4 mt-1 space-y-1">
              {laborCosts > 0 && (
                <div className="flex justify-between text-xs text-gray-500">
                  <span>• Hourly/Flat Rates</span>
                  <span>-${laborCosts.toLocaleString()}</span>
                </div>
              )}
              {commissionCosts > 0 && (
                <div className="flex justify-between text-xs text-gray-500">
                  <span>• Commissions{isLoadingCommissions ? ' (loading...)' : ''}</span>
                  <span>-${commissionCosts.toLocaleString()}</span>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-between text-sm mt-2">
            <span className="text-gray-600">Material Costs</span>
            <span className="text-gray-900">-${materialCosts.toLocaleString()}</span>
          </div>

          {discounts > 0 && (
            <div className="flex justify-between text-sm mt-2">
              <span className="text-gray-600">Discounts</span>
              <span className="text-red-600">-${discounts.toLocaleString()}</span>
            </div>
          )}

          {otherCosts > 0 && (
            <div className="flex justify-between text-sm mt-2">
              <span className="text-gray-600">Other Costs</span>
              <span className="text-gray-900">-${otherCosts.toLocaleString()}</span>
            </div>
          )}

          <div className="flex justify-between text-sm mt-4 pt-4 border-t border-gray-200">
            <span className="text-gray-600">Total Costs</span>
            <span className="text-gray-900">-${totalCosts.toLocaleString()}</span>
          </div>
        </div>

        <div>
          <div className="text-sm text-gray-600">Net Profit ({profitMargin.toFixed(1)}%)</div>
          <div className="flex items-center">
            {profit >= 0 ? (
              <TrendingUp className="h-4 w-4 text-green-500 mr-1.5" />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-500 mr-1.5" />
            )}
            <span className={`text-sm font-medium ${
              profit >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              ${Math.abs(profit).toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}